import type { PriceList, PriceListRow } from '@morales/shared';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../api';
import { DecoRule, OakMark } from '../components/brand';
import { Empty, PageHead, QueryView } from '../components/ui';
import { formatDate, formatMoney } from '../format';
import { useSearchParam } from '../hooks';
import { useLocale } from '../i18n';
import { qk } from '../queryClient';

/** The list as text: one line per item, for WhatsApp. */
export function priceListText(list: PriceList, locale: 'en' | 'es', intlLocale: string, title: string): string {
  const head = `${title} · ${list.customerName ?? list.tierName} · ${formatDate(list.effectiveDate, intlLocale)}`;
  const lines = list.rows.map((r) => `${locale === 'es' && r.nameEs ? r.nameEs : r.itemName}${r.packDesc ? ` (${r.packDesc})` : ''}: ${formatMoney(r.price, intlLocale)}`);
  return [head, '', ...lines].join('\n');
}

/** The list as a spreadsheet (CSV): item, pack, price. */
export function priceListCsv(list: PriceList): string {
  const esc = (v: string | number | null) => (v === null ? '' : /[",\n]/.test(String(v)) ? `"${String(v).replace(/"/g, '""')}"` : String(v));
  return ['item,item_es,pack,price', ...list.rows.map((r) => [r.itemName, r.nameEs, r.packDesc ?? r.packUnit, r.price].map(esc).join(','))].join('\n');
}

/**
 * How the printed sheet fits one letter page (JL: "we need to make it one sheet"): more columns and a smaller type as
 * the list grows. Counted in printed rows (each section's heading is a row too).
 */
export function sheetFit(rows: number): { cols: number; font: number } {
  // about 60 rows of 10.5 px type fill a column under the letterhead; smaller type and more columns hold more
  for (const [cols, font, perCol] of [[3, 10.5, 58], [4, 9.5, 64], [4, 8.5, 72], [5, 7.6, 72], [5, 7, 80]] as const) {
    if (rows <= cols * perCol) return { cols, font };
  }
  return { cols: 6, font: 6.4 };
}

export type PriceSection = { key: 'fruit' | 'veg' | 'dry' | 'other'; rows: PriceListRow[] };
/**
 * The printed list's sections, as JL's sheet has them: Fruits, Vegetables (herbs and chiles with them), Dry goods,
 * then anything else; by name inside each. Empty sections are left out.
 */
export function priceSections(rows: PriceListRow[], locale: 'en' | 'es' = 'en'): PriceSection[] {
  // by the name printed: the Spanish list is alphabetical in Spanish
  const nameOf = (r: PriceListRow) => (locale === 'es' && r.nameEs ? r.nameEs : r.itemName);
  const of = (c: string | null): PriceSection['key'] => {
    const k = (c ?? '').toLowerCase();
    if (k.startsWith('fruit')) return 'fruit';
    if (k.startsWith('veg') || k.startsWith('herb') || k.startsWith('chile')) return 'veg';
    if (k.startsWith('dry')) return 'dry';
    return 'other';
  };
  const order: PriceSection['key'][] = ['fruit', 'veg', 'dry', 'other'];
  return order
    .map((key) => ({ key, rows: rows.filter((r) => of(r.category) === key).sort((a, b) => nameOf(a).localeCompare(nameOf(b), locale)) }))
    .filter((sec) => sec.rows.length > 0);
}

export function PriceListPage() {
  const company = useQuery({ queryKey: qk.companySettings, queryFn: ({ signal }) => api.companySettings(signal), staleTime: 60 * 60_000, retry: false });
  const { t, locale, intlLocale } = useLocale();
  const [tier, setTier] = useSearchParam('tier');
  const [customer, setCustomer] = useSearchParam('customer');
  const [customerQ, setCustomerQ] = useState('');
  const [copied, setCopied] = useState(false);
  const tiers = useQuery({ queryKey: qk.priceTiers, queryFn: ({ signal }) => api.priceTiers(signal) });
  const customers = useQuery({ queryKey: qk.customers(customerQ), queryFn: ({ signal }) => api.customers(customerQ, signal), enabled: customerQ.trim().length > 0 });
  const key = customer ? `c:${customer}` : `t:${tier || 'standard'}`;
  const list = useQuery({ queryKey: qk.priceList(key), queryFn: ({ signal }) => api.priceList(customer ? { customer } : { tier: tier || 'standard' }, signal) });

  const download = (data: PriceList) => {
    const blob = new Blob([priceListCsv(data)], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `price-list-${(data.customerName ?? data.tierName).replace(/[^A-Za-z0-9]+/g, '-')}-${data.effectiveDate}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };
  const copy = async (data: PriceList) => {
    try {
      await navigator.clipboard.writeText(priceListText(data, locale, intlLocale, t('priceList')));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // clipboard blocked: the print view still works
    }
  };

  return (
    <div className="stack price-list">
      <PageHead title={t('priceList')} back="/prices" />
      <p className="muted no-print">{t('priceListHint')}</p>
      <div className="two-up no-print">
        <label className="field">
          <span>{t('forTier')}</span>
          <select
            value={customer ? '' : tier || 'standard'}
            onChange={(e) => {
              setCustomer('');
              setTier(e.target.value);
            }}
          >
            {customer ? <option value="">—</option> : null}
            {(tiers.data ?? []).filter((x) => x.active).map((x) => (
              <option key={x.code} value={x.code}>
                {x.name}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>{t('forCustomer')}</span>
          <input value={customerQ} onChange={(e) => setCustomerQ(e.target.value)} placeholder={t('search')} />
          {customers.data && customerQ.trim() ? (
            <select size={Math.min(5, Math.max(2, customers.data.length))} aria-label={t('forCustomer')} value={customer} onChange={(e) => { setCustomer(e.target.value); setCustomerQ(''); }}>
              {customers.data.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          ) : null}
        </label>
      </div>
      <QueryView query={list}>
        {(data) => (
          <>
            {/* no browser header or footer (the address and the date) on the printed sheet: the page has no margin of its
                own, the sheet keeps its own */}
            <style>{'@media print { @page { size: letter; margin: 0; } }'}</style>
            <header className="letterhead price-sheet-head">
              <OakMark />
              <span className="wordmark">
                <span className="wordmark-name">Morales Produce</span>
                <span className="wordmark-sub" data-testid="price-sheet-contact">
                  {[company.data?.phone, [company.data?.city || 'Bernalillo', company.data?.state || 'NM'].join(', ')].filter(Boolean).join(' · ')}
                </span>
              </span>
              <DecoRule />
              <h2 data-testid="price-sheet-title">
                {(data.customerName ?? data.tierName).toUpperCase()} · {t('priceList').toUpperCase()} — {formatDate(data.effectiveDate, intlLocale, 'date')}
              </h2>
            </header>
            <div className="quick-grid no-print">
              <button type="button" className="btn btn-primary" onClick={() => window.print()}>
                {t('print')}
              </button>
              <button type="button" className="btn btn-secondary" onClick={() => download(data)}>
                {t('download')}
              </button>
              <button type="button" className="btn btn-secondary" onClick={() => void copy(data)}>
                {copied ? t('copied') : t('copyText')}
              </button>
            </div>
            {data.rows.length === 0 ? (
              <Empty>{t('noResults')}</Empty>
            ) : (
              <div
                className="price-sheet"
                data-testid="price-list-rows"
                style={(() => {
                  const secs = priceSections(data.rows, locale);
                  const fit = sheetFit(data.rows.length + secs.length);
                  return { ['--pl-cols' as string]: String(fit.cols), ['--pl-font' as string]: `${fit.font}px` };
                })()}
              >
                {priceSections(data.rows, locale).map((sec) => (
                  <section key={sec.key} className="price-sheet-section">
                    <div className="price-sheet-row price-sheet-th">
                      {/* items without a category on a list with no other section are just the products */}
                      <span>{t(sec.key === 'other' && priceSections(data.rows, locale).length === 1 ? 'navProducts' : `priceSection_${sec.key}`)}</span>
                      <span>{t('priceCol')}</span>
                    </div>
                    {sec.rows.map((r) => (
                      <div key={r.itemId} className="price-sheet-row">
                        <span>
                          {locale === 'es' && r.nameEs ? r.nameEs : r.itemName}
                          {r.packDesc && !(r.itemName.toLowerCase().includes(r.packDesc.toLowerCase())) ? <span className="muted"> {r.packDesc}</span> : null}
                        </span>
                        <span className="price-sheet-price">{formatMoney(r.price, intlLocale)}</span>
                      </div>
                    ))}
                  </section>
                ))}
              </div>
            )}
            <footer className="price-sheet-foot" data-testid="price-sheet-foot">
              <DecoRule />
              <p>{t('priceListFoot')}</p>
            </footer>
          </>
        )}
      </QueryView>
    </div>
  );
}
